import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRp, waLink, parseRupiah, formatRupiahInput } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, CheckCircle, XCircle, Plus, Eye, Prohibit, DownloadSimple, BellRinging, WhatsappLogo, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";

const PAY_BADGE = {
  "Menunggu Verifikasi": "bg-amber-100 text-amber-700",
  "Terverifikasi": "bg-emerald-100 text-emerald-700",
  "Ditolak": "bg-rose-100 text-rose-700",
  "Void": "bg-slate-200 text-slate-500",
};

const repInput = "w-full rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

export default function Payments() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [fltStatus, setFltStatus] = useState("");
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [flt, setFlt] = useState({ start: "", end: "", jenis_layanan: "", method: "", payment_status: "", reservation_status: "", petugas: "" });
  const [opts, setOpts] = useState({ services: [], methods: [], staff: [] });
  const [search, setSearch] = useState("");
  const [jenis, setJenis] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = {};
    if (fltStatus) params.status = fltStatus;
    if (search.trim()) params.search = search.trim();
    if (jenis) params.jenis = jenis;
    if (flt.method) params.method = flt.method;
    if (flt.start) params.start = flt.start;
    if (flt.end) params.end = flt.end;
    if (flt.petugas) params.petugas = flt.petugas;
    if (flt.jenis_layanan) params.jenis_layanan = flt.jenis_layanan;
    api.get("/payments", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/payments/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [fltStatus, search, jenis, flt.method, flt.start, flt.end, flt.petugas, flt.jenis_layanan]);
  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/payment-methods"), api.get("/staff")])
      .then(([s, m, st]) => setOpts({ services: s.data, methods: m.data, staff: st.data })).catch(() => {});
  }, []);
  const setF = (k, v) => setFlt((f) => ({ ...f, [k]: v }));

  const verify = async (id) => { try { await api.post(`/payments/${id}/verify`); toast.success("Pembayaran diverifikasi"); load(); } catch (e) { toast.error(apiError(e)); } };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/payments/${reject.id}/reject`, { reason }); toast.success("Pembayaran ditolak"); setReject(null); setReason(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doVoid = async (id) => {
    const r = window.prompt("Alasan void/koreksi:");
    if (!r) return;
    try { await api.post(`/payments/${id}/void`, { reason: r }); toast.success("Pembayaran di-void"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const viewProof = async (fid) => {
    try {
      const res = await api.get(`/payments/proof/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const checkStatus = async (mid) => {
    if (!mid) return;
    try { await api.post(`/payments/midtrans/${mid}/reconcile`); toast.success("Status Midtrans diperbarui"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doExport = async (fmt) => {
    const params = { format: fmt };
    Object.entries(flt).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const res = await api.get("/export/payments", { params, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data);
      a.download = `Laporan_Pembayaran.${fmt === "csv" ? "csv" : "xlsx"}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Laporan pembayaran berhasil diunduh");
    } catch (e) { toast.error("Gagal mengekspor laporan"); }
  };

  const S = summary || {};
  const cards = [
    ["Menunggu Verifikasi", S.pending_verifikasi ?? 0, "#b45309", "#fef3c7", false],
    ["DP Masuk (hari ini)", S.dp_hari_ini ?? 0, "#6A38C7", "#EEE6FF", true],
    ["Pelunasan (hari ini)", S.pelunasan_hari_ini ?? 0, "#047857", "#d1fae5", true],
    ["Terverifikasi (hari ini)", S.terverifikasi_hari_ini ?? 0, "#0891b2", "#cffafe", true],
    ["Sisa Piutang", S.sisa_piutang ?? 0, "#be185d", "#fce7f3", true],
  ];

  return (
    <div className="animate-fade-up space-y-5" data-testid="payments-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Wallet size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Pembayaran</h1></div>
        <div className="flex gap-2">
          <button data-testid="dp-reminder-button" onClick={() => setRemindersOpen(true)} className="flex items-center gap-1.5 rounded-xl bg-amber-500 px-3 py-2 text-sm font-bold text-white hover:bg-amber-600"><BellRinging size={16} weight="bold" /> Pengingat DP</button>
          <button data-testid="manual-payment-button" onClick={() => setManualOpen(true)} className="flex items-center gap-1.5 rounded-xl bg-[#7C4DE0] px-3 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]"><Plus size={16} weight="bold" /> Pembayaran Manual</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="payments-summary">
        {cards.map(([label, val, color, bg, isRp]) => (
          <div key={label} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
            <span className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: bg, color }}><Wallet size={16} weight="duotone" /></span>
            <p className="font-heading text-xl font-extrabold text-slate-800">{isRp ? formatRp(val) : val}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm" data-testid="payment-report-filters">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="font-heading text-sm font-bold text-slate-700">Laporan Pembayaran</p>
          <div className="flex gap-2">
            <button data-testid="export-payments-xlsx" onClick={() => doExport("xlsx")} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-700"><DownloadSimple size={16} /> Excel</button>
            <button data-testid="export-payments-csv" onClick={() => doExport("csv")} className="flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-600"><DownloadSimple size={16} /> CSV</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Dari Tgl</label><input data-testid="rep-start" type="date" value={flt.start} onChange={(e) => setF("start", e.target.value)} className={repInput} /></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Sampai Tgl</label><input data-testid="rep-end" type="date" value={flt.end} onChange={(e) => setF("end", e.target.value)} className={repInput} /></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Layanan</label><select data-testid="rep-layanan" value={flt.jenis_layanan} onChange={(e) => setF("jenis_layanan", e.target.value)} className={repInput}><option value="">Semua</option>{opts.services.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}</select></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Metode</label><select data-testid="rep-method" value={flt.method} onChange={(e) => setF("method", e.target.value)} className={repInput}><option value="">Semua</option><option>Transfer Bank</option><option>QRIS</option><option>Refund</option>{opts.methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Payment Status</label><select data-testid="rep-pay-status" value={flt.payment_status} onChange={(e) => setF("payment_status", e.target.value)} className={repInput}><option value="">Semua</option>{["Belum Bayar", "Menunggu Verifikasi", "DP Terbayar", "Sebagian Terbayar", "Lunas", "Ditolak", "Refund Sebagian", "Refund Penuh", "Tidak Perlu Bayar"].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Status Reservasi</label><select data-testid="rep-res-status" value={flt.reservation_status} onChange={(e) => setF("reservation_status", e.target.value)} className={repInput}><option value="">Semua</option>{["Menunggu Pembayaran", "Menunggu Konfirmasi", "Dikonfirmasi", "Reschedule", "Batal"].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Petugas</label><select data-testid="rep-petugas" value={flt.petugas} onChange={(e) => setF("petugas", e.target.value)} className={repInput}><option value="">Semua</option>{opts.staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}</select></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="payments-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama / HP / kode / layanan / petugas / Midtrans ID"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-rose-400" />
        </div>
        <select data-testid="payments-filter-jenis" value={jenis} onChange={(e) => setJenis(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">Semua Jenis</option>
          <option value="single">Reservasi Tunggal</option>
          <option value="multi">Multi-Layanan</option>
        </select>
        <select data-testid="payments-filter-status" value={fltStatus} onChange={(e) => setFltStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">Semua Status</option>
          <option>Menunggu Verifikasi</option>
          <option>Terverifikasi</option>
          <option>Ditolak</option>
          <option>Void</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white shadow-sm">
        <table className="w-full min-w-[1050px] text-sm">
          <thead><tr className="border-b border-rose-100 text-left text-xs font-bold text-slate-500">
            <th className="p-3">Pasien / Kode</th><th className="p-3">Layanan</th><th className="p-3">Petugas</th>
            <th className="p-3">Pelayanan</th><th className="p-3">Lokasi</th><th className="p-3">Nominal</th>
            <th className="p-3">Metode</th><th className="p-3">Status</th><th className="p-3">Transaksi</th><th className="p-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-rose-50 last:border-0 align-top">
                <td className="p-3">
                  <span className="font-semibold text-slate-700">{p.nama_pasien || "-"}</span><br />
                  <span className="text-[11px] text-slate-400">{p.kode || "-"}</span>
                  <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${p.is_order ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{p.is_order ? "Multi" : "Tunggal"}</span>
                </td>
                <td className="p-3 text-slate-600">{p.jenis_layanan || "-"}</td>
                <td className="p-3 text-slate-600">{p.petugas_nama || "-"}</td>
                <td className="p-3 text-xs text-slate-500">{p.tanggal_kunjungan || "-"}<br />{p.jam_kunjungan || ""}</td>
                <td className="p-3 text-xs text-slate-500">{p.lokasi || "-"}</td>
                <td className="p-3 font-bold text-slate-800">{formatRp(p.amount)}{p.kind === "refund" && <span className="ml-1 text-xs text-rose-500">(refund)</span>}<br /><span className="text-[10px] font-normal text-slate-400">sisa {formatRp(p.sisa_tagihan)}</span></td>
                <td className="p-3">
                  <span className="text-slate-600">{p.method}</span>
                  {p.method_type === "midtrans" && <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">Midtrans</span>}
                  {p.method_type === "cash" && <span className="block text-[10px] text-slate-400">Pembayaran Tunai</span>}
                </td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${PAY_BADGE[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status}</span></td>
                <td className="p-3 text-[11px] text-slate-400">{(p.created_at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    <button data-testid={`detail-${p.id}`} onClick={() => setDetail(p.id)} className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">Detail</button>
                    {p.method_type === "manual" && (p.has_proof
                      ? <button data-testid={`view-proof-${p.id}`} onClick={() => viewProof(p.proof_file_id)} className="rounded-lg bg-slate-100 p-1.5 text-slate-600" title="Lihat bukti"><Eye size={15} /></button>
                      : <span className="rounded-lg bg-slate-50 px-2 py-1 text-[11px] text-slate-400">Belum ada bukti</span>)}
                    {p.method_type === "midtrans" && p.midtrans_order_id && <button data-testid={`check-${p.id}`} onClick={() => checkStatus(p.midtrans_order_id)} className="rounded-lg bg-indigo-100 p-1.5 text-indigo-700" title="Cek Status Midtrans"><ArrowsClockwise size={15} /></button>}
                    {p.status === "Menunggu Verifikasi" && <>
                      <button data-testid={`verify-${p.id}`} onClick={() => verify(p.id)} className="rounded-lg bg-emerald-500 p-1.5 text-white" title="Verifikasi"><CheckCircle size={15} weight="bold" /></button>
                      <button data-testid={`reject-${p.id}`} onClick={() => { setReject(p); setReason(""); }} className="rounded-lg bg-rose-500 p-1.5 text-white" title="Tolak"><XCircle size={15} weight="bold" /></button>
                    </>}
                    {isSuper && p.status === "Terverifikasi" && <button data-testid={`void-${p.id}`} onClick={() => doVoid(p.id)} className="rounded-lg bg-slate-200 p-1.5 text-slate-600" title="Void"><Prohibit size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-sm text-slate-400">Belum ada pembayaran.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reject} onOpenChange={(v) => !v && setReject(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="reject-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Tolak Pembayaran</DialogTitle></DialogHeader>
          <textarea data-testid="reject-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan penolakan wajib diisi" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
          <DialogFooter>
            <button onClick={() => setReject(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="confirm-reject" onClick={doReject} className="rounded-xl bg-rose-500 px-5 py-2 text-sm font-bold text-white">Tolak</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manualOpen && <ManualDialog isSuper={isSuper} onClose={() => setManualOpen(false)} onDone={load} />}
      {remindersOpen && <RemindersDialog onClose={() => setRemindersOpen(false)} />}
      {detail && <PaymentDetailModal pid={detail} onClose={() => setDetail(null)} onProof={viewProof} onCheck={checkStatus} />}
    </div>
  );
}

function DRow({ l, v }) {
  return <div className="flex justify-between gap-4 border-b border-rose-50 py-1.5 text-sm last:border-0"><span className="text-slate-500">{l}</span><span className="text-right font-semibold text-slate-800">{v}</span></div>;
}

function PaymentDetailModal({ pid, onClose, onProof, onCheck }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/payments/${pid}/detail`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [pid]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="payment-detail-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Detail Pembayaran</DialogTitle></DialogHeader>
        {!d ? <p className="py-6 text-center text-sm text-slate-400">Memuat…</p> : (
          <div className="space-y-3">
            <div>
              <DRow l="Pasien / Pemesan" v={d.nama_pasien || "-"} />
              <DRow l="Kode" v={d.kode || "-"} />
              <DRow l="Jenis" v={d.jenis_transaksi} />
              <DRow l="Layanan" v={d.jenis_layanan || "-"} />
              <DRow l="Petugas" v={d.petugas_nama || "-"} />
              <DRow l="Tanggal / Jam" v={`${d.tanggal_kunjungan || "-"} ${d.jam_kunjungan || ""}`} />
              <DRow l="Lokasi" v={d.lokasi || "-"} />
            </div>
            {d.is_order && d.items && d.items.length > 0 && (
              <div className="rounded-xl bg-slate-50 p-2" data-testid="detail-items">
                {d.items.map((it, i) => <div key={i} className="flex justify-between py-0.5 text-xs"><span className="text-slate-600">{it.jenis_layanan} · {it.nama_pasien} · {it.jam_kunjungan} · {it.petugas_nama}</span><span className="font-semibold">{formatRp(it.total_tagihan)}</span></div>)}
              </div>
            )}
            <div>
              <DRow l="Total Tagihan" v={formatRp(d.total_tagihan)} />
              <DRow l="Total Dibayar" v={formatRp(d.total_dibayar)} />
              <DRow l="Sisa" v={formatRp(d.sisa_tagihan)} />
              <DRow l="Nominal Transaksi Ini" v={formatRp(d.amount)} />
              <DRow l="Metode" v={d.method} />
              <DRow l="Status" v={d.status} />
              {d.midtrans_order_id && <DRow l="Midtrans Order ID" v={d.midtrans_order_id} />}
              <DRow l="Tgl Pembayaran" v={(d.created_at || "").replace("T", " ").slice(0, 16)} />
            </div>
            <div className="flex flex-wrap gap-2">
              {d.method_type === "manual" && d.has_proof && <button data-testid="detail-view-proof" onClick={() => onProof(d.proof_file_id)} className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">Lihat Bukti Transfer</button>}
              {d.method_type === "manual" && !d.has_proof && <span className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-400">Belum ada bukti transfer</span>}
              {d.method_type === "midtrans" && d.midtrans_order_id && <button data-testid="detail-check-status" onClick={() => onCheck(d.midtrans_order_id)} className="rounded-xl bg-indigo-100 px-3 py-2 text-xs font-bold text-indigo-700">Cek Status Midtrans</button>}
            </div>
            {d.related_payments && d.related_payments.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-bold text-slate-500">Riwayat Pembayaran Terkait</p>
                {d.related_payments.map((rp) => <div key={rp.id} className="flex justify-between border-b border-rose-50 py-1 text-xs last:border-0"><span className="text-slate-600">{formatRp(rp.amount)} · {rp.method} · {rp.kind}</span><span className="font-semibold text-slate-700">{rp.status}</span></div>)}
              </div>
            )}
          </div>
        )}
        <DialogFooter><button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Tutup</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualDialog({ isSuper, onClose, onDone }) {
  const [reservations, setReservations] = useState([]);
  const [rid, setRid] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Tunai");
  const [kind, setKind] = useState("manual");
  const [note, setNote] = useState("");
  const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm";

  useEffect(() => { api.get("/reservations").then((r) => setReservations(r.data.slice(0, 300))).catch(() => {}); }, []);

  const submit = async () => {
    if (!rid) return toast.error("Pilih reservasi");
    const amt = parseRupiah(amount);
    if (!amt || amt <= 0) return toast.error("Nominal tidak valid");
    try {
      if (kind === "refund") await api.post("/payments/refund", { reservation_id: rid, amount: amt, reason: note || "Refund" });
      else await api.post("/payments/manual", { reservation_id: rid, amount: amt, method, kind, note });
      toast.success("Pembayaran tercatat"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="manual-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Pembayaran Manual</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Reservasi</label>
            <select data-testid="manual-reservation" value={rid} onChange={(e) => setRid(e.target.value)} className={inputCls}>
              <option value="">-- Pilih Reservasi --</option>
              {reservations.map((r) => <option key={r.id} value={r.id}>{r.nama_lengkap} · {r.tanggal_kunjungan} · {r.jenis_layanan}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nominal</label><input data-testid="manual-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(formatRupiahInput(e.target.value))} placeholder="cth: 150.000" className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Jenis</label>
              <select data-testid="manual-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls}>
                <option value="manual">Pembayaran</option>
                <option value="dp">DP</option>
                <option value="pelunasan">Pelunasan</option>
                {isSuper && <option value="refund">Refund</option>}
              </select>
            </div>
          </div>
          {kind !== "refund" && <div><label className="mb-1 block text-xs font-semibold text-slate-500">Metode</label>
            <select data-testid="manual-method" value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
              <option>Tunai</option><option>Transfer</option><option>QRIS</option><option>Lainnya</option>
            </select></div>}
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Catatan {kind === "refund" && "(alasan wajib)"}</label><input data-testid="manual-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
          <button data-testid="manual-submit" onClick={submit} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white">Simpan</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemindersDialog({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/reminders").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const buildMsg = (r) => {
    const dl = r.payment_deadline ? new Date(r.payment_deadline).toLocaleString("id-ID") : "-";
    const dpLine = r.payment_mode === "dp" ? `• DP yang perlu dibayar: ${formatRp(r.dp_required)}\n` : "";
    return `Halo ${r.nama_pasien} 🤍\nMengingatkan reservasi Anda di GBS Clinic & Birth Center:\n• Layanan: ${r.jenis_layanan}\n• Tanggal: ${r.tanggal_kunjungan} — ${r.jam_kunjungan}\n• Total: ${formatRp(r.total_tagihan)}\n${dpLine}• Batas pembayaran: ${dl}\nMohon segera lakukan pembayaran agar reservasi dikonfirmasi. Terima kasih 🤍`;
  };
  const fmtLeft = (m) => (m == null ? "—" : m <= 0 ? "Lewat batas" : m < 60 ? `${m}m lagi` : `${Math.floor(m / 60)}j ${m % 60}m lagi`);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="reminders-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Pengingat DP — Belum Bayar</DialogTitle></DialogHeader>
        {loading ? (
          <p className="py-6 text-center text-sm text-slate-400">Memuat...</p>
        ) : items.length === 0 ? (
          <p data-testid="reminders-empty" className="py-6 text-center text-sm text-slate-400">Tidak ada reservasi menunggu pembayaran. 🎉</p>
        ) : (
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.id} data-testid={`reminder-${r.id}`} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${r.near_deadline ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
                <div className="min-w-0">
                  <p className="truncate font-bold text-slate-800">{r.nama_pasien}</p>
                  <p className="truncate text-xs text-slate-500">{r.jenis_layanan} · {r.tanggal_kunjungan} {r.jam_kunjungan}</p>
                  <p className="text-xs font-semibold text-slate-600">{r.payment_mode === "dp" ? `DP ${formatRp(r.dp_required)}` : `Total ${formatRp(r.total_tagihan)}`} · <span className={r.near_deadline ? "text-amber-600" : "text-slate-500"}>{fmtLeft(r.mins_left)}</span></p>
                </div>
                <a data-testid={`reminder-wa-${r.id}`} href={waLink(r.no_whatsapp, buildMsg(r))} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#25D366] px-3 py-2 text-xs font-bold text-white active:scale-95"><WhatsappLogo size={16} weight="fill" /> Kirim WA</a>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Tutup</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

